import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import config from '../config';
import { getBffHeaders } from '../utils/apiHeaders';
import { getAuthorizedFetch } from '../utils/fetchRegistry';
import './AIInstructionsModal.css';

const AI_API_PREFIX = 'api/catalog';

// ── Playbook data helpers ─────────────────────────────────────────────────────

function getPlaybookAgentRefs(playbook) {
  if (Array.isArray(playbook.agents) && playbook.agents.length > 0) return playbook.agents;
  return ((playbook.flow?.nodes) || [])
    .filter(n => n.type === 'agent' && n.ref)
    .map(n => ({ itemId: n.ref, id: n.ref, name: n.ref }));
}

function getPlaybookModels(playbook) {
  if (Array.isArray(playbook.models) && playbook.models.length > 0) return playbook.models;
  if (playbook.model) return [{ name: playbook.model }];
  const agentRefs = getPlaybookAgentRefs(playbook);
  const models = [...new Set(
    agentRefs.map(a => a.model || a.resources?.model || a.llm_args?.model).filter(Boolean)
  )];
  return models.map(m => ({ name: m }));
}

function resolveRefNames(items) {
  return (items || []).map(r => (typeof r === 'string' ? r : (r.name || r.itemId))).filter(Boolean);
}

/** Normalise any item (string | ref-object | full-object) to a display object */
function normaliseItem(raw) {
  if (typeof raw === 'string') return { name: raw, itemId: null, description: null, role: null, model: null, tools: [], skills: [], prompts: [] };
  return {
    name:        raw.name || raw.itemId || raw.ref || raw.id || '—',
    itemId:      raw.itemId || raw.id   || null,
    description: raw.description || raw.goal || raw.instructions || null,
    role:        raw.role  || raw.type  || null,
    model:       raw.model || raw.resources?.model || raw.llm_args?.model || null,
    tools:       Array.isArray(raw.tools)  ? raw.tools  : [],
    skills:      Array.isArray(raw.skills) ? raw.skills : [],
    prompts:     Array.isArray(raw.prompts)? raw.prompts : [],
  };
}

// ── Sub-components ────────────────────────────────────────────────────────────

/**
 * A single item card — recursive so nested tools/skills render at a deeper indent.
 */
function ItemCard({ raw, depth = 0 }) {
  const item = normaliseItem(raw);
  const showId = item.itemId && item.itemId !== item.name;

  const subSections = [
    { icon: '⚡', label: 'Skills', items: item.skills  },
    { icon: '🔧', label: 'Tools',  items: item.tools   },
    { icon: '💬', label: 'Prompts',items: item.prompts },
  ].filter(s => s.items.length > 0);

  return (
    <div className={`pb-item-card pb-item-depth-${Math.min(depth, 2)}`}>
      <div className="pb-item-header">
        <span className="pb-item-name">{item.name}</span>
        {item.role  && <span className="pb-item-tag">{item.role}</span>}
        {item.model && <span className="pb-item-tag pb-item-model">🧠 {item.model}</span>}
      </div>
      {showId      && <div className="pb-item-id">{item.itemId}</div>}
      {item.description && <div className="pb-item-desc">{item.description}</div>}

      {subSections.map(s => (
        <div className="pb-sub-group" key={s.label}>
          <div className="pb-sub-group-label">{s.icon} {s.label} <span className="pb-sub-count">{s.items.length}</span></div>
          <div className="pb-sub-group-items">
            {s.items.map((sub, i) => <ItemCard key={i} raw={sub} depth={depth + 1} />)}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * A full-width box for one section (Agents / Prompts / Skills / Specs).
 */
function SectionBox({ icon, label, items }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="pb-section-box">
      <div className="pb-section-header">
        <span className="pb-section-icon">{icon}</span>
        <span className="pb-section-label">{label}</span>
        <span className="pb-section-count">{items.length}</span>
      </div>
      <div className="pb-section-items">
        {items.map((item, i) => <ItemCard key={i} raw={item} />)}
      </div>
    </div>
  );
}

const SECTIONS = [
  { icon: '🤖', label: 'Agents',  get: pb => pb._enrichedAgents  ?? getPlaybookAgentRefs(pb) },
  { icon: '💬', label: 'Prompts', get: pb => pb._enrichedPrompts ?? (pb.prompts || []) },
  { icon: '⚡', label: 'Skills',  get: pb => pb._enrichedSkills  ?? (pb.skills  || []) },
  { icon: '📋', label: 'Specs',   get: pb => pb._enrichedSpecs   ?? (pb.specs   || []) },
];

/**
 * Full detail view for a playbook.
 * showName=true → shows the playbook name + mode badge (for the library preview panel).
 */
function PlaybookDetail({ playbook, showName = false }) {
  const models = getPlaybookModels(playbook);
  const hasSections = SECTIONS.some(s => s.get(playbook).length > 0);

  return (
    <div className="pb-detail-view">
      {showName && (
        <div className="pb-detail-title-row">
          <span className="pb-detail-name">{playbook.name}</span>
          {playbook.mode && (
            <span className={`playbook-mode-badge-sm playbook-mode-${playbook.mode}`}>
              {playbook.mode === 'code' ? '💻 Code' : playbook.mode === 'both' ? '💬💻 Both' : '💬 Chat'}
            </span>
          )}
        </div>
      )}

      {playbook.description && (
        <p className="pb-detail-description">{playbook.description}</p>
      )}

      {models.length > 0 && (
        <div className="pb-models-row">
          {models.map((m, i) => (
            <span key={i} className="pb-model-chip">🧠 {m.name || m}</span>
          ))}
        </div>
      )}

      {(playbook.homeProjectTemplateId || playbook.homeSourceTemplateId) && (
        <div className="pb-templates-row">
          {playbook.homeProjectTemplateId && (
            <span className="pb-template-chip" title="Home project template">🏗 {playbook.homeProjectTemplateId}</span>
          )}
          {playbook.homeSourceTemplateId && (
            <span className="pb-template-chip" title="Home source template">📦 {playbook.homeSourceTemplateId}</span>
          )}
        </div>
      )}

      {(playbook.showOnHome || playbook.homePrompt) && (
        <div className="pb-home-row">
          {playbook.showOnHome && (
            <span className="pb-home-chip" title="Shown on home screen">🏠 Home</span>
          )}
          {playbook.homePrompt && (
            <span className="pb-home-prompt" title="Home prompt">💬 {playbook.homePrompt}</span>
          )}
        </div>
      )}

      {hasSections ? (
        SECTIONS.map(s => (
          <SectionBox key={s.label} icon={s.icon} label={s.label} items={s.get(playbook)} />
        ))
      ) : (
        <div className="pb-no-items">No components configured for this playbook.</div>
      )}
    </div>
  );
}

// ── Main modal component ──────────────────────────────────────────────────────

/** Fetch one catalog item by family + itemId, return the detail object or null. */
async function fetchCatalogDetail(family, itemId, tenantId) {
  if (!itemId || !tenantId) return null;
  try {
    const baseUrl = config.bffUrl || window.location.origin;
    const res = await getAuthorizedFetch()(
      `${baseUrl}/${AI_API_PREFIX}/${family}/${tenantId}/${itemId}/latest`,
      { headers: getBffHeaders() }
    );
    if (!res.ok) return null;
    const json = await res.json();
    if (!json.success) return null;
    // Different families use different envelope keys
    return json.agent || json.prompt || json.skill || json.spec || json.data || null;
  } catch {
    return null;
  }
}

/** Enrich a playbook by fetching full detail for each ref in agents/prompts/skills/specs. */
async function enrichPlaybook(playbook, tenantId) {
  if (!playbook || !tenantId) return playbook;

  const enrichItems = (family, refs) =>
    Promise.all((refs || []).map(async (ref) => {
      const itemId = typeof ref === 'string' ? ref : (ref.itemId || ref.id);
      const detail = await fetchCatalogDetail(family, itemId, tenantId);
      if (!detail) return typeof ref === 'string' ? { name: ref } : ref;
      return { ...(typeof ref === 'object' ? ref : {}), ...detail };
    }));

  const [enrichedAgents, enrichedPrompts, enrichedSkills, enrichedSpecs] = await Promise.all([
    enrichItems('ai-agents',  getPlaybookAgentRefs(playbook)),
    enrichItems('ai-prompts', playbook.prompts || []),
    enrichItems('ai-skills',  playbook.skills  || []),
    enrichItems('ai-specs',   playbook.specs   || []),
  ]);

  return {
    ...playbook,
    _enrichedAgents:  enrichedAgents,
    _enrichedPrompts: enrichedPrompts,
    _enrichedSkills:  enrichedSkills,
    _enrichedSpecs:   enrichedSpecs,
  };
}

const AIInstructionsModal = ({
                               isOpen,
                               onClose,
                               onApply,
                               currentInstructions = '',
                               currentPlaybookData = null,
                               tenantId = null,
                             }) => {
  const [playbooks, setPlaybooks] = useState([]);
  const [isLoadingPlaybooks, setIsLoadingPlaybooks] = useState(false);
  const [selectedPlaybook, setSelectedPlaybook] = useState(null);
  const [error, setError] = useState(null);
  // Enriched versions of the displayed playbook (with full detail fetched from API)
  const [enrichedSelected, setEnrichedSelected] = useState(null);
  const [enrichedActive, setEnrichedActive] = useState(null);
  const [isEnriching, setIsEnriching] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSelectedPlaybook(null);
      setEnrichedSelected(null);
      setError(null);
    }
  }, [isOpen]);

  // Enrich active playbook whenever the modal opens (or active playbook changes)
  useEffect(() => {
    if (!isOpen || !currentPlaybookData || !tenantId) return;
    setIsEnriching(true);
    enrichPlaybook(currentPlaybookData, tenantId)
      .then(setEnrichedActive)
      .catch(() => setEnrichedActive(currentPlaybookData))
      .finally(() => setIsEnriching(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, currentPlaybookData?.itemId, tenantId]);

  // Enrich selected playbook whenever user picks one from the library
  useEffect(() => {
    if (!selectedPlaybook || !tenantId) {
      setEnrichedSelected(null);
      return;
    }
    setIsEnriching(true);
    enrichPlaybook(selectedPlaybook, tenantId)
      .then(setEnrichedSelected)
      .catch(() => setEnrichedSelected(selectedPlaybook))
      .finally(() => setIsEnriching(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlaybook?.itemId, tenantId]);

  const fetchPlaybooks = useCallback(async () => {
    if (!tenantId) {
      setError('No tenant selected.');
      return;
    }
    setIsLoadingPlaybooks(true);
    setError(null);
    try {
      const params = new URLSearchParams({ tenantId, page: '1', perPage: '100' });
      const baseUrl = config.bffUrl || window.location.origin;
      const response = await getAuthorizedFetch()(`${baseUrl}/${AI_API_PREFIX}/ai-playbooks?${params.toString()}`, {
        headers: getBffHeaders(),
      });
      if (!response.ok) throw new Error('Failed to fetch AI playbooks');
      const result = await response.json();
      if (result.success && result.data) {
        setPlaybooks(result.data.items || []);
      } else {
        throw new Error(result.error || 'Failed to fetch playbooks');
      }
    } catch (err) {
      console.error('Error fetching playbooks:', err);
      setError(err.message || 'Failed to load playbooks');
      setPlaybooks([]);
    } finally {
      setIsLoadingPlaybooks(false);
    }
  }, [tenantId]);

  useEffect(() => {
    if (isOpen && !currentPlaybookData) {
      fetchPlaybooks();
    }
  }, [isOpen, currentPlaybookData, fetchPlaybooks]);

  const buildInstructions = (playbook) => {
    const agentNames  = getPlaybookAgentRefs(playbook).map(a => a.name || a.itemId).filter(Boolean);
    const promptNames = resolveRefNames(playbook.prompts);
    const specNames   = resolveRefNames(playbook.specs);
    const skillNames  = resolveRefNames(playbook.skills);
    let text = `[PLAYBOOK: ${playbook.name}]\n`;
    if (playbook.description) text += `${playbook.description}\n\n`;
    if (agentNames.length)  text += `Active agents: ${agentNames.join(', ')}\n`;
    if (promptNames.length) text += `Loaded prompts: ${promptNames.join(', ')}\n`;
    if (specNames.length)   text += `Loaded specs: ${specNames.join(', ')}\n`;
    if (skillNames.length)  text += `Active skills: ${skillNames.join(', ')}\n`;
    return text;
  };

  const handleActivate = () => {
    if (!selectedPlaybook) return;
    onApply(buildInstructions(selectedPlaybook), selectedPlaybook.name, null, selectedPlaybook);
    onClose();
  };

  const handleDeactivate = () => {
    onApply('', '', null, null);
    onClose();
  };

  if (!isOpen) return null;

  // Quick summary badges for the library list cards
  const listBadges = (pb) => [
    { icon: '🤖', count: getPlaybookAgentRefs(pb).length,  label: 'agents'  },
    { icon: '💬', count: (pb.prompts || []).length,         label: 'prompts' },
    { icon: '⚡', count: (pb.skills  || []).length,         label: 'skills'  },
    { icon: '📋', count: (pb.specs   || []).length,         label: 'specs'   },
    { icon: '🧠', count: getPlaybookModels(pb).length,      label: 'models'  },
  ].filter(b => b.count > 0);

  return ReactDOM.createPortal(
    <div className="ai-instructions-modal-overlay" onClick={onClose}>
      <div className="ai-instructions-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="ai-instructions-header">
          <div className="ai-instructions-title-group">
            <h2 className="ai-instructions-title">
              <span className="ai-instructions-icon">🎯</span>
              {currentPlaybookData ? 'Active AI Playbook' : 'AI Playbook Library'}
            </h2>
            {currentPlaybookData && (
              <span className="playbook-active-subtitle">{currentPlaybookData.name}</span>
            )}
          </div>
          <button className="ai-instructions-close" onClick={onClose} type="button" aria-label="Close">×</button>
        </div>

        {/* Sub-header */}
        <p className="ai-instructions-description">
          {currentPlaybookData
            ? 'This playbook is active for the current session — agents, skills, specs and prompts are pre-configured.'
            : 'Select an AI Playbook to configure agents, skills, specs and prompts for this session.'}
        </p>

        {/* Content */}
        <div className="ai-instructions-content">
          {currentPlaybookData ? (
            // ── ACTIVE PLAYBOOK DETAIL ────────────────────────────────────
            <div className="pb-scroll-area">
              {isEnriching && !enrichedActive ? (
                <div className="marketplace-loading"><div className="loading-spinner"></div>Loading details…</div>
              ) : (
                <PlaybookDetail playbook={enrichedActive || currentPlaybookData} />
              )}
            </div>
          ) : (
            // ── PLAYBOOK LIBRARY ──────────────────────────────────────────
            <div className="ai-instructions-marketplace">
              {isLoadingPlaybooks ? (
                <div className="marketplace-loading">
                  <div className="loading-spinner"></div>
                  Loading AI Playbooks…
                </div>
              ) : error ? (
                <div className="marketplace-error">⚠️ {error}</div>
              ) : playbooks.length === 0 ? (
                <div className="playbook-empty-state">
                  <div className="playbook-empty-icon">🎯</div>
                  <div className="playbook-empty-title">No AI Playbooks found</div>
                  <div className="playbook-empty-desc">
                    Create your playbooks in the AI Playbook Library section of the catalog.
                  </div>
                </div>
              ) : (
                <div className="marketplace-two-column">
                  {/* Left: playbook list */}
                  <div className="marketplace-prompts-list">
                    {playbooks.map(pb => (
                      <button
                        key={pb.itemId}
                        className={`playbook-list-card ${selectedPlaybook?.itemId === pb.itemId ? 'selected' : ''}`}
                        onClick={() => setSelectedPlaybook(pb)}
                        type="button"
                      >
                        <div className="playbook-list-card-header">
                          <span className="playbook-list-card-name">{pb.name}</span>
                          {pb.mode && (
                            <span className={`playbook-mode-badge-sm playbook-mode-${pb.mode}`}>
                              {pb.mode === 'code' ? '💻' : '💬'} {pb.mode}
                            </span>
                          )}
                        </div>
                        {pb.description && (
                          <div className="playbook-list-card-desc">{pb.description}</div>
                        )}
                        {listBadges(pb).length > 0 && (
                          <div className="playbook-list-card-badges">
                            {listBadges(pb).map(b => (
                              <span key={b.label} className="playbook-count-badge" title={b.label}>
                                {b.icon} {b.count}
                              </span>
                            ))}
                          </div>
                        )}
                      </button>
                    ))}
                  </div>

                  {/* Right: selected playbook full detail */}
                  {selectedPlaybook ? (
                    <div className="marketplace-preview-panel pb-scroll-area">
                      {isEnriching && !enrichedSelected ? (
                        <div className="marketplace-loading"><div className="loading-spinner"></div>Loading details…</div>
                      ) : (
                        <PlaybookDetail playbook={enrichedSelected || selectedPlaybook} showName />
                      )}
                    </div>
                  ) : (
                    <div className="playbook-select-hint">
                      <div className="playbook-select-hint-icon">👈</div>
                      <div>Select a playbook to see its details</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="ai-instructions-footer">
          <div className="footer-left">
            {currentPlaybookData && (
              <button
                className="ai-instructions-button secondary danger"
                onClick={handleDeactivate}
                type="button"
                title="Deactivate the playbook and return to free mode"
              >
                🗑 Deactivate Playbook
              </button>
            )}
          </div>
          <div className="footer-right">
            <button className="ai-instructions-button secondary" onClick={onClose} type="button">
              {currentPlaybookData ? 'Close' : 'Cancel'}
            </button>
            {!currentPlaybookData && (
              <button
                className="ai-instructions-button primary"
                onClick={handleActivate}
                disabled={!selectedPlaybook}
                type="button"
              >
                🎯 Activate Playbook
              </button>
            )}
          </div>
        </div>

      </div>
    </div>,
    document.body
  );
};

AIInstructionsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
  currentInstructions: PropTypes.string,
  currentPlaybookData: PropTypes.object,
  tenantId: PropTypes.string,
};

export default AIInstructionsModal;
